import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { usePostHog } from 'posthog-js/react'
import { useSetRecoilState } from 'recoil'

import { axios } from '@/api/axios.config'
import { getFuneralHome } from '@/api/funeralHomes'
import { user as userApi } from '@/api/users'

import { userAtom } from '@/atoms/_globalAtoms'
import { userFuneralHomeAtom } from '@/atoms/globalAtoms'

import { Auth } from '@/components/auth/Auth'
import { BetaWelcome } from '@/components/dashboard/BetaWelcome/BetaWelcome'
import { EditUnauthorized } from '@/components/landing/EditUnauthorized'
import { Landing } from '@/components/landing/Landing'
import { NewUser } from '@/components/landing/NewUser/NewUser'
import { PdfRender } from '@/components/pdfRender/PdfRender'

import { useAdminValidation } from '@/hooks/utility/useAdminValidation'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'

import type { APIError } from '@/types/api'
import type {
  AuthProviderProps,
  AuthSession,
  LogoutOptions,
} from '@/types/auth'

import { api } from '@/utils/global/api'
import { isLocal } from '@/utils/global/env'
import { resolveClientBypass } from '@messenger/utils/local-auth-bypass'
import {
  identifyUserInPosthog,
  resetPostHogState,
} from '@/utils/global/posthog'
import {
  clearOrderSession,
  orderSessionId,
  adoptSharedOrder,
  tabOrderId,
} from '@/utils/global/orderSession'

// URL prefixes that participate in the order-token (x-order) editor flow.
// On these paths an edit-link order this tab opened is considered active and
// must not be cleared by the Auth0 stale-token cleanup. Trailing slash is
// load-bearing — `/edit-unauthorized` must NOT match `/editor/`.
export const EDITOR_FLOW_PATH_PREFIXES = ['/auth/', '/preview/', '/editor/']

export const isEditorFlowPath = (pathname: string) =>
  EDITOR_FLOW_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))

/**
 * Whether this tab runs as an edit link rather than as the Auth0 session: it
 * is on a link's sign-in page (`/auth/`), where the link is being opened, or
 * on another editor-flow path after its own edit link opened an order. An
 * order another tab's link left in the shared key never decides it, so a
 * signed-in user who opens an order's editor from the dashboard keeps the
 * JWT. A tab opened before tabs kept their own order holds one once
 * `adoptSharedOrder` has run.
 */
export const usesOrderTokenAuth = (pathname: string): boolean =>
  pathname.startsWith('/auth/') ||
  (!!tabOrderId() && isEditorFlowPath(pathname))

const AuthProvider = ({
  children,
  isAuthenticated,
  session,
}: AuthProviderProps) => {
  const handleError = useErrorHandler('AuthProvider')
  const setUser = useSetRecoilState(userAtom)
  const setFuneralHome = useSetRecoilState(userFuneralHomeAtom)
  const [token, setToken] = useState(session.initialToken ?? '')
  const [error, setError] = useState('')
  const [headerSet, setHeaderSet] = useState(
    isAuthenticated || !!session.initialToken,
  )
  const logout = usePerformLogout()
  const posthog = usePostHog()

  const location = [window.location.pathname]

  const shouldBypassAuthenticationForProofGallery =
    location[0].startsWith('/proof-gallery/') ||
    location[0].startsWith('/proof-share/')

  const { user, getAccessTokenSilently, isLoading } = session

  const editLinkOrderId = orderSessionId()

  useEffect(() => {
    if (!isAuthenticated) {
      if (user) {
        getAccessTokenSilently().then(setToken)
      }
    }
  }, [user, getAccessTokenSilently, isAuthenticated, editLinkOrderId])

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      // Auth0 JWT takes over — clear any stale Messenger session so the
      // x-order header doesn't override the authenticated user identity.
      // Skip while on active editor-flow URLs, where Auth.js may have just
      // set the token for the x-order BE resolution path. Without this
      // guard, an admin landing on /auth/<id> with an existing Auth0
      // session can have the freshly-set token wiped out before the
      // redirect to /preview/ completes.
      if (!isEditorFlowPath(window.location.pathname)) {
        clearOrderSession()
      }
      setHeaderSet(true)
    }
  }, [token])

  useEffect(() => {
    if (!isAuthenticated) {
      ;(async () => {
        if (token) {
          try {
            const { data: loggedInUser, status } = await userApi()
            const home = await getFuneralHome().catch(function (
              error: APIError,
            ) {
              if (
                error.response &&
                'Missing funeral home identification.' ===
                  error.response.data?.message
              ) {
                logout({
                  returnTo: `${window.location.origin}/new-user`,
                  redirect: true,
                })
              }
            })

            // TODO: uncomment this when we have a backend fix
            // const { data: logos } = await getFuneralHomeLogos()

            if (loggedInUser.id) {
              setUser(loggedInUser)

              try {
                // Identify user in PostHog (this is important for linking recordings to users)
                identifyUserInPosthog(posthog, loggedInUser, home?.id)
              } catch (error) {
                console.error('PostHog identification failed:', error)
              }
            } else if (status === 401) {
              setError('User error')
            } else {
              setError('Display a Toast?')
              logout({ returnTo: window.location.origin, redirect: true })
            }
            if (home?.id) {
              setFuneralHome(home)
            }
          } catch (error) {
            const err = error as APIError & Error
            handleError(err?.response?.data?.message || err.message, err)
          }
        }
      })()
    }
    // eslint-disable-next-line
  }, [token, logout, posthog])

  if (shouldBypassAuthenticationForProofGallery) {
    return <>{children}</>
  }

  return (
    <>
      {location[0].includes('auth') ? (
        <Auth />
      ) : location[0].includes('new-user') ? (
        <NewUser />
      ) : location[0].includes('edit-unauthorized') ? (
        <EditUnauthorized />
      ) : location[0].includes('pdfProduct') ? (
        <PdfRender proof={false} />
      ) : location[0].includes('pdfProofProduct') ? (
        <PdfRender proof={true} />
      ) : location[0].includes('beta-welcome') ? (
        <BetaWelcome />
      ) : (!isAuthenticated && isLoading && !user?.id) ||
        (!headerSet && !isAuthenticated) ? (
        <Landing error={null} />
      ) : (
        children
      )}
      {error && <Landing error={error} />}
    </>
  )
}

const Auth0Session = ({
  children,
  isAuthenticated,
}: Omit<AuthProviderProps, 'session'>) => {
  const { user, getAccessTokenSilently, isLoading } = useAuth0()
  return (
    <AuthProvider
      isAuthenticated={isAuthenticated}
      session={{ user, getAccessTokenSilently, isLoading }}
    >
      {children}
    </AuthProvider>
  )
}

// The email the local stack signs in as, or undefined when the app must run
// real Auth0. The decision is the same fail-closed switch the backend uses:
// the flag, a development build, and a localhost API — plus a localhost page,
// so the email is never sent anywhere but a local backend that honors it.
export const authBypassEmail = (): string | undefined => {
  const email = process.env.REACT_APP_AUTH_BYPASS_EMAIL
  const decision = resolveClientBypass({
    flag: process.env.REACT_APP_LOTUS_LOCAL_AUTH_BYPASS,
    email,
    nodeEnv: import.meta.env.DEV ? 'development' : 'production',
    hostname: window.location.hostname,
    apiUrl: api,
  })
  if (decision.email === undefined && email) {
    console.error(
      `🔓 REACT_APP_AUTH_BYPASS_EMAIL is set but the local auth bypass stays off: ${decision.reason}`,
    )
  }
  return decision.email
}

// The bearer is the email itself: the backend's bypass validator resolves the
// user by it, so the app boots signed in with no Auth0 round trip. An edit
// link (x-order) carries no bearer, exactly as it does beside a real session.
export const bypassSession = (
  email: string,
  { orderScoped = false }: { orderScoped?: boolean } = {},
): AuthSession => ({
  user: { sub: `bypass|${email}`, email },
  isLoading: false,
  getAccessTokenSilently: async () => email,
  initialToken: orderScoped ? undefined : email,
})

export const installBypassBearer = (email: string): void => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${email}`
}

const authEnvsAvailable = (): boolean => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN as string | undefined
  const clientId = process.env.REACT_APP_AUTH0_CLIENTID as string | undefined
  const audience = process.env.REACT_APP_AUTH0_AUDIENCE as string | undefined

  if (!domain || !clientId || !audience) {
    throw new Error(
      'Missing required Auth0 environment variables. Check REACT_APP_AUTH0_DOMAIN, REACT_APP_AUTH0_CLIENTID, and REACT_APP_AUTH0_AUDIENCE',
    )
  }
  return true
}

export const Auth0 = ({ children }: PropsWithChildren) => {
  useAdminValidation()
  // Use the order-token-only auth path ONLY when the tab is on a link's
  // sign-in page (`/auth/`), or when this tab's own edit link opened an order
  // and the URL is an active editor flow. Otherwise mount
  // Auth0Provider so an existing Auth0 session takes over, and off the editor
  // the stale edit-link order is cleared automatically.
  // A tab opened before tabs kept their own order takes it now (idempotent),
  // so the mode below and every header read the tab's own order.
  adoptSharedOrder()
  const useOrderTokenAuth = usesOrderTokenAuth(window.location.pathname)
  const bypassEmail = authBypassEmail()
  const bypass = useMemo(() => {
    if (!bypassEmail) return undefined
    if (!useOrderTokenAuth) installBypassBearer(bypassEmail)
    return bypassSession(bypassEmail, { orderScoped: useOrderTokenAuth })
  }, [bypassEmail, useOrderTokenAuth])

  if (bypass) {
    return (
      <AuthProvider isAuthenticated={useOrderTokenAuth} session={bypass}>
        {children}
      </AuthProvider>
    )
  }

  if (!authEnvsAvailable()) {
    return null
  }

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN!}
      clientId={process.env.REACT_APP_AUTH0_CLIENTID!}
      audience={process.env.REACT_APP_AUTH0_AUDIENCE!}
      redirectUri={window.location.origin}
      // Localhost-only: persist tokens in localStorage and renew via refresh
      // tokens so a full page reload (typing a URL in the address bar) keeps
      // the session. Without this, the iframe silent-auth path fails on
      // http://localhost because Chrome blocks third-party cookies to the
      // Auth0 domain, forcing a re-login that loses the intended destination.
      // Production keeps the default memory cache + cookie-based silent auth.
      cacheLocation={isLocal ? 'localstorage' : 'memory'}
      useRefreshTokens={isLocal}
    >
      <Auth0Session isAuthenticated={useOrderTokenAuth}>
        {children}
      </Auth0Session>
    </Auth0Provider>
  )
}

export default Auth0

export const usePerformLogout = () => {
  const { logout, user } = useAuth0()
  const setUser = useSetRecoilState(userAtom)
  const posthog = usePostHog()

  return useCallback(
    ({ redirect = true, redirectTo = '/' }: LogoutOptions = {}) => {
      const isAuthenticatedWithLocalStorage = !!orderSessionId()

      if (isAuthenticatedWithLocalStorage) {
        clearOrderSession()
        // Clear session validation flags
        sessionStorage.removeItem('tokenValidated')
        sessionStorage.removeItem('tokenValidatedAt')
        setUser(null)
      }

      // Reset PostHog tracking on logout
      posthog?.reset()
      resetPostHogState()

      if (user) {
        logout({
          returnTo: window.location.origin,
        })
      } else if (redirect) {
        window.location.href = redirectTo
      }
    },
    [logout, setUser, user, posthog],
  )
}
